import { styled } from '@mui/material/styles';
import { useDraggable } from '@dnd-kit/core';
import BrandingWatermarkOutlinedIcon from '@mui/icons-material/BrandingWatermarkOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import SmartButtonOutlinedIcon from '@mui/icons-material/SmartButtonOutlined';
import HorizontalRuleOutlinedIcon from '@mui/icons-material/HorizontalRuleOutlined';
import HeightOutlinedIcon from '@mui/icons-material/HeightOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import SplitscreenOutlinedIcon from '@mui/icons-material/SplitscreenOutlined';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { EMAIL_BLOCK_TYPE, EMAIL_PALETTE_BLOCK_TYPES } from '@constants/campaignTemplates';
import { BLOCK_LABEL_KEYS } from './designUtils';

const TwoColumnsIcon = styled(SplitscreenOutlinedIcon)({
    transform: 'rotate(90deg)',
});

export const BLOCK_ICONS = {
    [EMAIL_BLOCK_TYPE.LOGO]: BrandingWatermarkOutlinedIcon,
    [EMAIL_BLOCK_TYPE.IMAGE]: ImageOutlinedIcon,
    [EMAIL_BLOCK_TYPE.HEADING]: TitleOutlinedIcon,
    [EMAIL_BLOCK_TYPE.TEXT]: NotesOutlinedIcon,
    [EMAIL_BLOCK_TYPE.BUTTON]: SmartButtonOutlinedIcon,
    [EMAIL_BLOCK_TYPE.COLUMNS]: TwoColumnsIcon,
    [EMAIL_BLOCK_TYPE.DIVIDER]: HorizontalRuleOutlinedIcon,
    [EMAIL_BLOCK_TYPE.SPACER]: HeightOutlinedIcon,
    [EMAIL_BLOCK_TYPE.SOCIAL]: ShareOutlinedIcon,
};

export const PALETTE_DRAG_PREFIX = 'palette:';

const Panel = styled('div')({
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
});

const PaletteGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
});

const PaletteItem = styled('button')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    border: '1px solid var(--divider)',
    borderRadius: 8,
    background: '#fff',
    padding: '14px 8px',
    cursor: 'grab',
    fontFamily: 'inherit',
    fontSize: 13,
    color: 'var(--text-primary)',
    touchAction: 'none',
    '& > svg': {
        width: 22,
        height: 22,
        color: 'var(--text-secondary)',
    },
    '&:hover': {
        borderColor: 'var(--primary-main)',
        '& > svg': {
            color: 'var(--primary-main)',
        },
    },
    '&:disabled': {
        cursor: 'default',
        opacity: 0.5,
    },
});

const DraggableBlock = ({ type, disabled, onAddBlock }) => {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: `${PALETTE_DRAG_PREFIX}${type}`,
        disabled,
    });
    const Icon = BLOCK_ICONS[type];

    return (
        <PaletteItem
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            type="button"
            disabled={disabled}
            style={{ opacity: isDragging ? 0.4 : 1 }}
            onClick={() => onAddBlock(type)}
        >
            <Icon />
            {t(BLOCK_LABEL_KEYS[type])}
        </PaletteItem>
    );
};

const BlockPalette = ({ onAddBlock, isView }) => {
    const { t } = useTranslation();

    return (
        <Panel>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {t('TemplateLibrary.paletteHint')}
            </Typography>
            <PaletteGrid>
                {EMAIL_PALETTE_BLOCK_TYPES.map(type => (
                    <DraggableBlock key={type} type={type} disabled={isView} onAddBlock={onAddBlock} />
                ))}
            </PaletteGrid>
        </Panel>
    );
};

export default BlockPalette;
